/* global __TRACE_ROOT__ */
import { createRoot } from 'react-dom/client';
import GlobalStyles from '@styles/GlobalStyles';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import App from './App.jsx';
import { registerMediaPipeSw } from '@/features/movexBuilder/services/registerMediaPipeSw';
import { initSentry } from '@libs/sentry';
import { initMixpanel } from '@inPerformance/services/analytics';
import { installNetworkMeter } from '@inPerformance/services/network-meter';
import { logRuntimeConfig } from './inPerformance/constants/domain';
import '@/styles/index.css';
import './i18n';

// Development-only network usage meter. First, so its WebSocket / RTCPeerConnection
// / fetch taps exist before anything else can create one. No-op in production.
installNetworkMeter();

// Initialise error/perf monitoring before anything else renders.
initSentry();
initMixpanel();

// Which API/socket/voice-flag this bundle was actually built with — the only way
// to tell a missing build-arg from a backend problem on a deployed image.
logRuntimeConfig();

const queryClient = new QueryClient();

function normalizeTraceRoot(root) {
  if (!root || typeof root !== 'string') return root;

  const windowsMountMatch = root.match(/^\/mnt\/([a-zA-Z])\/(.*)$/);
  if (!windowsMountMatch) return root;

  const [, driveLetter, restPath] = windowsMountMatch;
  return `${driveLetter.toUpperCase()}:/${restPath}`;
}

async function bootstrap() {
  if (typeof globalThis !== 'undefined' && !globalThis.global) {
    globalThis.global = globalThis;
  }

  // Register the MediaPipe SW as early as possible so it controls the page and
  // can intercept/cache the model + WASM before the pose worker fetches them.
  void registerMediaPipeSw();

  const { default: Trace } = await import('@react-trace/kit');

  const traceRoot = normalizeTraceRoot(__TRACE_ROOT__);

  createRoot(document.getElementById('root')).render(
    <QueryClientProvider client={queryClient}>
      <GlobalStyles />
      <App />
      <Trace root={traceRoot} position="bottom-left" editor="vscode" editingDisabled={false} />
    </QueryClientProvider>
  );
}

bootstrap();
